// stores/pwa/index.js
import { defineStore } from 'pinia'

export const useInstallPromptStore = defineStore('installPrompt', {
    state: () => ({
        // États d'affichage
        showPrompt: false,
        dismissed: false,

        // Versions
        currentVersion: null,
        installedVersion: null,
        dismissedVersion: null,
        dismissedDate: null,

        // États vite-pwa
        needRefresh: false,
        offlineReady: false,

        // Fonction de mise à jour vite-pwa
        updateSW: null,

        // Service Worker registration
        registration: null,

        // Support navigateur
        browserSupport: {
            canInstall: false,
            hasPrompt: false,
            browserName: null,
            isStandalone: false
        },

        // BeforeInstallPrompt event (pour Chrome/Edge)
        deferredPrompt: null
    }),

    getters: {
        /**
         * Détermine si le prompt doit être affiché
         */
        shouldShowPrompt() {
            if (this.needRefresh) return true

            if (this.browserSupport.isStandalone) return false

            // Pour Chrome/Edge : afficher SEULEMENT si deferredPrompt existe
            if (this.browserSupport.hasPrompt) {
                return this.deferredPrompt !== null
            }

            // Pour Safari/Firefox : afficher si pas dismissé
            if (this.browserSupport.canInstall && !this.dismissed) {
                return this.hasEnoughTimeElapsed
            }

            return false
        },


        /**
         * Vérifie si assez de temps s'est écoulé depuis le dismiss
         */
        hasEnoughTimeElapsed() {
            if (!this.dismissedDate) return true

            const config = useRuntimeConfig()
            const daysSince = (Date.now() - new Date(this.dismissedDate).getTime()) / (1000 * 60 * 60 * 24)
            const DAYS_THRESHOLD = config.public.pwaDaysThreshold || 7

            return daysSince > DAYS_THRESHOLD
        },

        /**
         * Type d'installation/mise à jour
         */
        installType() {
            if (this.needRefresh) return 'update'
            return 'install'
        },

        /**
         * Vérifie si on peut afficher les instructions manuelles
         */
        canShowManualInstructions() {
            return (
                !this.browserSupport.hasPrompt &&
                !this.browserSupport.isStandalone &&
                ['safari', 'firefox'].includes(this.browserSupport.browserName?.toLowerCase() || '')
            )
        }
    },

    actions: {
        /**
         * Initialise le store PWA
         */
        async init(appVersion) {
            console.log('🚀 Initialisation PWA Store...')

            // IMPORTANT: Écouter beforeinstallprompt EN PREMIER
            this.setupInstallPrompt()

            this.currentVersion = appVersion
            this.detectBrowserSupport()

            // Initialiser vite-pwa après
            await this.initVitePWA()

            // Vérifier l'état initial
            this.checkInitialState()
        },

        /**
         * Détecte le support du navigateur
         */
        detectBrowserSupport() {
            const ua = navigator.userAgent
            let browserName = 'Unknown'

            if (ua.includes('Chrome') && !ua.includes('Edg')) {
                browserName = 'Chrome'
            } else if (ua.includes('Firefox')) {
                browserName = 'Firefox'
            } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
                browserName = 'Safari'
            } else if (ua.includes('Edg')) {
                browserName = 'Edge'
            }

            // Vérifier si l'app est déjà installée
            const isStandalone =
                window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator?.standalone === true ||
                document.referrer.includes('android-app://')

            // Vérifier si le navigateur supporte beforeinstallprompt
            const hasPrompt = 'onbeforeinstallprompt' in window

            this.browserSupport = {
                canInstall: hasPrompt || ['safari', 'firefox'].includes(browserName.toLowerCase()),
                hasPrompt,
                browserName,
                isStandalone
            }

            console.log('📱 Browser support:', this.browserSupport)
        },

        /**
         * Initialise vite-pwa
         */
        async initVitePWA() {
            try {
                // Importer le module virtuel vite-pwa
                const { registerSW } = await import('virtual:pwa-register')

                // Enregistrer le service worker avec les callbacks
                this.updateSW = registerSW({
                    immediate: true,

                    onNeedRefresh: () => {
                        console.log('🔄 Nouvelle version disponible (vite-pwa)')
                        this.needRefresh = true
                        this.show()
                    },

                    onOfflineReady: () => {
                        console.log('✅ Application prête hors ligne')
                        this.offlineReady = true

                        // Optionnel: afficher une notification
                        if (!this.browserSupport.isStandalone) {
                            this.showOfflineReadyNotification()
                        }
                    },

                    onRegistered: (registration) => {
                        console.log('✅ Service Worker enregistré')
                        this.registration = registration

                        // Vérifier périodiquement les mises à jour
                        if (registration) {
                            setInterval(() => {
                                registration.update()
                            }, 60 * 60 * 1000) // Toutes les heures
                        }
                    },

                    onRegisterError: (error) => {
                        console.error('❌ Erreur enregistrement Service Worker:', error)
                    }
                })

                console.log('✅ vite-pwa initialisé')

            } catch (error) {
                console.warn('⚠️ vite-pwa non disponible:', error)
                // En développement ou si vite-pwa n'est pas configuré
                this.initFallback()
            }
        },

        /**
         * Fallback si vite-pwa n'est pas disponible
         */
        async initFallback() {
            if ('serviceWorker' in navigator) {
                try {
                    const registration = await navigator.serviceWorker.getRegistration()
                    if (registration) {
                        this.registration = registration
                        console.log('📦 Service Worker trouvé (fallback)')
                    }
                } catch (error) {
                    console.warn('Service Worker non disponible:', error)
                }
            }
        },

        /**
         * Configure l'événement beforeinstallprompt
         */
        setupInstallPrompt() {
            // IMPORTANT: Écouter l'événement AVANT tout le reste
            // Capturer l'événement le plus tôt possible
            window.addEventListener('beforeinstallprompt', (e) => {
                console.log('📲 Event beforeinstallprompt capturé!', e)
                e.preventDefault()
                this.deferredPrompt = e

                // Vérifier après un court délai
                setTimeout(() => {
                    if (!this.browserSupport.isStandalone) {
                        this.show()
                    }
                }, 100)
            })

            // Écouter l'installation
            window.addEventListener('appinstalled', () => {
                console.log('✅ PWA installée')
                this.handleInstallationComplete()
            })

            // ✅ Écouter les changements de display-mode
            const mediaQuery = window.matchMedia('(display-mode: standalone)')
            mediaQuery.addEventListener('change', (e) => {
                console.log('Display mode changed:', e.matches)
                if (e.matches) {
                    this.handleInstallationComplete()
                }
            })

            // Vérifier si l'événement a déjà été déclenché
            console.log('Setup complete, waiting for beforeinstallprompt...')
        },

        /**
         * ✅ Nouvelle méthode pour gérer l'installation complète
         */
        handleInstallationComplete() {
            console.log('🎉 Installation PWA détectée')

            // Nettoyer les états d'installation
            this.deferredPrompt = null

            // Sauvegarder l'installation
            this.saveInstallPrompt()

            // ✅ Cacher le prompt SEULEMENT s'il ne s'agit pas d'une mise à jour
            if (!this.needRefresh) {
                this.hide()
            }

            console.log('État après installation:', {
                dismissed: this.dismissed,
                showPrompt: this.showPrompt,
                shouldShow: this.shouldShowPrompt,
                needRefresh: this.needRefresh
            })
        },
        /**
         * Vérifie l'état initial
         */
        checkInitialState() {
            // Si peut installer et pas encore dismissé récemment
            if (this.browserSupport.canInstall && !this.dismissed) {
                setTimeout(() => {
                    if (!this.browserSupport.isStandalone) {
                        this.show()
                    }
                }, 30000) // Attendre 30 secondes après le chargement
            }
        },

        /**
         * Affiche le prompt
         */
        show() {
            if (this.shouldShowPrompt) {
                this.showPrompt = true
                console.log('👁️ Prompt affiché')
            }
        },

        /**
         * Cache le prompt
         */
        hide() {
            this.showPrompt = false
            console.log('🙈 Prompt caché')
        },

        /**
         * Applique la mise à jour
         */
        async applyUpdate() {
            console.log('🔄 Application de la mise à jour...')

            try {
                // Utiliser l'API vite-pwa si disponible
                if (this.updateSW) {
                    console.log('🔄 Rechargement pour mise à jour...')
                    this.installedVersion = this.currentVersion
                    this.needRefresh = false  // Marquer comme traité
                    this.hide()
                    await this.updateSW(true) // true = recharger la page
                }
                else {
                    // Fallback: recharger directement
                    console.log('♻️ Rechargement de la page...')
                    window.location.reload()
                }


            } catch (error) {
                console.error('❌ Erreur lors de la mise à jour:', error)
                // En cas d'erreur, forcer le rechargement
                window.location.reload()
            }
        },

        /**
         * Installe la PWA (Chrome/Edge)
         */
        async installPWA() {
            if (!this.deferredPrompt) {
                console.warn('⚠️ Pas d\'événement beforeinstallprompt disponible')
                return { success: false }
            }

            try {
                // Afficher le prompt natif
                this.deferredPrompt.prompt()

                // Attendre le choix de l'utilisateur
                const { outcome } = await this.deferredPrompt.userChoice
                console.log(`Choix utilisateur: ${outcome}`)

                if (outcome === 'accepted') {
                    return { success: true, outcome }
                }
                this.deferredPrompt = null
                return { success: false, outcome }

            } catch (error) {
                console.error('Erreur installation PWA:', error)
                return { success: false }
            }
        },

        /**
         * Sauvegarde l'état après installation
         */
        saveInstallPrompt() {
            this.installedVersion = this.currentVersion
            this.dismissed = true
            this.dismissedDate = null
            this.dismissedVersion = null
        },

        /**
         * Dismiss le prompt
         */
        dismiss() {
            this.dismissed = true
            this.dismissedDate = new Date().toISOString()
            this.dismissedVersion = this.currentVersion
            this.hide()

            console.log('👋 Prompt dismissé jusqu\'au',
                new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
            )
        },

        /**
         * Réinitialise le store (pour debug)
         */
        reset() {
            this.dismissed = false
            this.dismissedDate = null
            this.dismissedVersion = null
            this.showPrompt = false
            this.needRefresh = false
            this.offlineReady = false
            console.log('🔄 Store réinitialisé')
        },

        /**
         * Force l'affichage (pour debug)
         */
        forceShow() {
            this.showPrompt = true
        },

        /**
         * Affiche une notification offline ready
         */
        showOfflineReadyNotification() {
            // Utiliser votre système de notification
            const { $toast } = useNuxtApp()
            if ($toast) {
                $toast.success('Application disponible hors ligne')
            }
        },

        /**
         * Retourne les instructions manuelles selon le navigateur
         */
        getManualInstallInstructions() {
            const browser = this.browserSupport.browserName

            const instructions = {
                Safari: {
                    icon: 'heroicons:share',
                    steps: [
                        'Appuyez sur le bouton de partage en bas',
                        'Faites défiler et sélectionnez "Sur l\'écran d\'accueil"',
                        'Appuyez sur "Ajouter" en haut à droite'
                    ]
                },
                Firefox: {
                    icon: 'heroicons:bars-3',
                    steps: [
                        'Ouvrez le menu (≡) en haut à droite',
                        'Sélectionnez "Installer l\'application"',
                        'Confirmez l\'installation'
                    ]
                }
            }

            return instructions[browser || ''] || null
        },

        /**
         * Retourne les infos de debug
         */
        getDebugInfo() {
            return {
                state: {
                    showPrompt: this.showPrompt,
                    dismissed: this.dismissed,
                    needRefresh: this.needRefresh,
                    offlineReady: this.offlineReady,
                    deferredPrompt: !!this.deferredPrompt,
                    updateSW: !!this.updateSW,
                    registration: !!this.registration
                },
                browser: this.browserSupport,
                versions: {
                    current: this.currentVersion,
                    installed: this.installedVersion,
                    dismissed: this.dismissedVersion
                },
                computed: {
                    shouldShow: this.shouldShowPrompt,
                    type: this.installType
                }
            }
        }
    },

    persist: {
        key: 'pwa',
        paths: [
            'dismissed',
            'dismissedDate',
            'dismissedVersion',
            'installedVersion'
        ],
        secure: process.env.NODE_ENV === 'production'
    }
})